import { isEmpty as _isEmpty } from 'lodash-es'

import { MembershipLevel } from '@/view-models/member'
import type { Member } from '@/view-models/member'

export function useUserInfo() {
  const { $auth } = useNuxtApp()
  // userInfo 可能的值：Member、null、undefined、false
  // TODO: 之後將 userInfo 優化成較單純的 type
  const userInfo = computed<Member | null>(() => $auth.user as any)
  const levelName = computed<string | undefined>(() => userInfo.value?.benefit?.level_name)
  const isVip = computed(() => {
    const level = userInfo.value?.benefit?.level
    const isUpLevel = level === MembershipLevel.Elite || level === MembershipLevel.Pro

    return isUpLevel
  })

  const isLoggedIn = computed(() => !!userInfo.value)

  return {
    userInfo,
    levelName,
    isVip,
    isLoggedIn,
  }
}

export function useElite(product: any) {
  const { userInfo } = useUserInfo()

  const isDisabledByElite = computed(() => product.is_elite && userInfo.value?.is_elite === false)

  return {
    isDisabledByElite,
  }
}

const LEVEL_SEEKER_CONFIG = {
  membershipLevelIcon: 'SEEKER.svg',
  membershipLevelColor: 'seekerColor',
  membershipLevelColorClass: 'wd-text-orange50',
  membershipLevelFlatIcon: 'SEEKER_flat-enabled.svg',
  avatarImagePath: 'funnow_avatar_seeker.svg',
  avatarBorderClass: 'wd-border-seekerColor',
  linearGradientClass: 'wd-bg-gradient-to-b wd-from-orange10',
  commenterAvatarImagePath: 'comment_avatar_seeker.svg',
  funCoinsImages: ['/images/membership/funCoins-seeker-1.png', '/images/membership/funCoins-seeker-2.png'],
  funMilesImages: [
    '/images/membership/funmiles-seeker-1.png',
    '/images/membership/funmiles-seeker-2.png',
    '/images/membership/funmiles-seeker-3.png',
  ],
}
const LEVEL_PRO_CONFIG = {
  membershipLevelIcon: 'PRO.svg',
  membershipLevelColor: 'proColor',
  membershipLevelColorClass: 'wd-text-proColor',
  membershipLevelFlatIcon: 'PRO_flat-enabled.svg',
  avatarImagePath: 'funnow_avatar_pro.svg',
  avatarBorderClass: 'wd-border-proColor',
  linearGradientClass: 'wd-bg-gradient-to-b wd-from-brown20',
  commenterAvatarImagePath: 'comment_avatar_pro.svg',
  funCoinsImages: ['/images/membership/funCoins-pro-1.png', '/images/membership/funCoins-pro-2.png'],
  funMilesImages: ['/images/membership/funmiles-pro-1.png', '/images/membership/funmiles-pro-2.png', '/images/membership/funmiles-pro-3.png'],
}
const LEVEL_ELITE_CONFIG = {
  membershipLevelIcon: 'ELITE.svg',
  membershipLevelColor: 'eliteColor',
  membershipLevelColorClass: 'wd-text-eliteColor',
  membershipLevelFlatIcon: 'ELITE_flat-enabled.svg',
  avatarImagePath: 'funnow_avatar_elite.svg',
  avatarBorderClass: 'wd-border-eliteColor',
  linearGradientClass: 'wd-bg-gradient-to-b wd-from-pink20',
  commenterAvatarImagePath: 'comment_avatar_elite.svg',
  funCoinsImages: ['/images/membership/funCoins-elite-1.png', '/images/membership/funCoins-elite-2.png'],
  funMilesImages: ['/images/membership/funmiles-elite-1.png', '/images/membership/funmiles-elite-2.png', '/images/membership/funmiles-elite-3.png'],
}

const LEVEL_CONFIG_MAP = {
  [MembershipLevel.Seeker]: LEVEL_SEEKER_CONFIG,
  [MembershipLevel.Pro]: LEVEL_PRO_CONFIG,
  [MembershipLevel.Elite]: LEVEL_ELITE_CONFIG,
}

export function useMembershipLevel(infoParam: MaybeRef<any>) {
  const info = computed(() => unref(infoParam))
  const level = computed<MembershipLevel>(() => info.value?.member_level ?? info.value?.benefit?.level ?? MembershipLevel.Seeker)
  const levelConfig = computed(() => LEVEL_CONFIG_MAP[level.value])
  const membershipLevelName = computed(() => info.value?.member_level_name || info.value?.benefit?.level_name)

  const membershipLevelIcon = computed(() => levelConfig.value.membershipLevelIcon)
  const membershipLevelColor = computed(() => levelConfig.value.membershipLevelColor)
  const membershipLevelColorClass = computed(() => levelConfig.value.membershipLevelColorClass)
  const linearGradientClass = computed(() => !_isEmpty(info.value) && levelConfig.value.linearGradientClass)

  const membershipLevelFlatIcon = computed(() => levelConfig.value.membershipLevelFlatIcon)

  return {
    levelConfig,
    membershipLevelIcon,
    membershipLevelName,
    membershipLevelColor,
    membershipLevelColorClass,
    linearGradientClass,
    membershipLevelFlatIcon,
  }
}

export function useFunCoin() {
  const { userInfo } = useUserInfo()
  const { levelConfig } = useMembershipLevel(userInfo)

  const funCoinsImages = computed(() => levelConfig.value.funCoinsImages || [])

  return {
    funCoinsImages,
  }
}

export function useFunMiles() {
  const { userInfo } = useUserInfo()
  const { levelConfig } = useMembershipLevel(userInfo)

  const funMilesImages = computed(() => levelConfig.value.funMilesImages || [])

  return {
    funMilesImages,
  }
}

export function useAvatar(info: any, isFunBookV2 = ref(false)) {
  const { levelConfig, membershipLevelColor: avatarBackgroundColor } = useMembershipLevel(info)

  const avatarImagePath = computed(() => {
    if (isFunBookV2.value) {
      return 'funbook_avatar_default.svg'
    }

    return levelConfig.value.avatarImagePath
  })

  const avatarBorderClass = computed(() => levelConfig.value.avatarBorderClass)

  const commenterAvatarImagePath = computed(() => levelConfig.value.commenterAvatarImagePath)

  return {
    avatarImagePath,
    avatarBorderClass,
    commenterAvatarImagePath,
    avatarBackgroundColor,
  }
}
